/**
 * CutieroidDoll native preorder integration. Independently implemented.
 * It adds no requests, credentials, replacement checkout, or order quota.
 * Browser clock/consent checks are UX guards, not a security boundary.
 * Shopify customer-account settings and the separate Flow close are required.
 */
const SHOP = '74dbig-ju.myshopify.com';
const PRODUCT = '15218891260145';
const VARIANT = '67142756794609';
const WIDGET = '[data-cutieroid-preorder]';
const OWNED = '[data-cp-owned]';
const ACCELERATED = '.shopify-payment-button, shopify-accelerated-checkout, .shopify-payment-button__button, [data-shopify="payment-button"]';
const STICKY = 'sticky-add-to-cart';
const PLAN_NAME = 'selling_plan';
const CONSENT_NAME = 'properties[予約条件への同意]';
const DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(Z|([+-])(\d{2}):(\d{2}))$/;

function timestamp(value) {
  if (typeof value !== 'string') return NaN;
  const match = value.match(DATE_TIME);
  if (!match) return NaN;
  const [, y, m, d, h, minute, second, , , offsetHour, offsetMinute] = match;
  if (+y < 1970 || +m < 1 || +m > 12 || +d < 1 || +d > new Date(Date.UTC(+y, +m, 0)).getUTCDate() || +h > 23 || +minute > 59 || +second > 59 || +(offsetHour || 0) > 14 || +(offsetMinute || 0) > 59 || (+offsetHour === 14 && +offsetMinute !== 0)) return NaN;
  return Date.parse(value);
}

function japanDateTime(ms) {
  return new Intl.DateTimeFormat('ja-JP', {timeZone: 'Asia/Tokyo', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'}).format(ms) + '（日本時間）';
}

function numberString(value) { return typeof value === 'string' && /^\d+$/.test(value); }
function valueOf(control) { return String(control.value ?? control.getAttribute('value') ?? ''); }
function element(node) { return node && node.nodeType === 1 ? node : node?.parentElement; }
function setText(node, text) { if (node && node.textContent !== text) node.textContent = text; }
function setAttribute(node, name, value) { if (node.getAttribute(name) !== value) node.setAttribute(name, value); }

/** Reads only server-rendered metadata. A matching ID alone is insufficient:
 * the selected formal variant must allocate the exact plan at full, unchanged price.
 */
export function readWidget(widget) {
  try {
    const c = JSON.parse(widget.getAttribute('data-config') || 'null');
    if (!c || c.enabled !== true) return {active: false, reason: 'disabled'};
    const plan = typeof c.sellingPlanId === 'string' && c.sellingPlanId.match(/^gid:\/\/shopify\/SellingPlan\/(\d+)$/)?.[1];
    if (c.id !== 'gorai-2026' || c.version !== 1 || c.shopDomain !== SHOP || c.productId !== `gid://shopify/Product/${PRODUCT}` || c.variantId !== `gid://shopify/ProductVariant/${VARIANT}` || c.paymentMode !== 'full' || !plan || !/^gid:\/\/shopify\/SellingPlanGroup\/\d+$/.test(c.sellingPlanGroupId || '')) return {active: false, reason: 'configuration'};
    if (widget.getAttribute('data-shop-domain') !== SHOP || widget.getAttribute('data-product-id') !== PRODUCT || widget.getAttribute('data-variant-id') !== VARIANT) return {active: false, reason: 'scope'};
    const start = timestamp(c.startAt), end = timestamp(c.endAt), cutoff = timestamp(c.cancelCutoff);
    if (![start, end, cutoff].every(Number.isFinite) || start >= end || cutoff < start || cutoff >= end || !Number.isInteger(c.cancelHours) || c.cancelHours < 1 || c.cancelHours > 168) return {active: false, reason: 'dates'};
    if (typeof c.termsText !== 'string' || !c.termsText.trim() || c.termsText.length > 8000 || typeof c.deliveryNote !== 'string' || !c.deliveryNote.trim() || c.deliveryNote.length > 2000) return {active: false, reason: 'terms'};
    const release = typeof c.releaseDate === 'string' && c.releaseDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!release || +release[1] < 1970 || +release[2] < 1 || +release[2] > 12 || +release[3] < 1 || +release[3] > new Date(Date.UTC(+release[1], +release[2], 0)).getUTCDate()) return {active: false, reason: 'release'};
    const allocations = JSON.parse(widget.getAttribute('data-allocations') || '[]');
    const allocation = Array.isArray(allocations) && allocations.find(a => a.sellingPlanId === plan);
    const price = widget.getAttribute('data-variant-price');
    if (!allocation || !numberString(price) || allocation.price !== price || allocation.checkoutChargeAmount !== price || allocation.remainingBalanceChargeAmount !== '0') return {active: false, reason: 'allocation'};
    return {active: true, c, plan, start, end, cutoff, widget, fingerprint: JSON.stringify([c, allocation, price])};
  } catch { return {active: false, reason: 'configuration'}; }
}

export function createPreorderController(doc, {now = () => Date.now(), autoTick = true} = {}) {
  const win = doc.defaultView || globalThis;
  const states = new Map();
  const paymentOwners = new Map();
  const buttonOwners = new Map();
  // Horizon routes clicks from document capture. Block before that routing,
  // including its optimistic animation, regardless of listener registration order.
  const eventRoot = typeof win.addEventListener === 'function' ? win : doc;
  const stats = {refreshes: 0};
  let disposed = false, queued = false, timer = null;
  const enqueue = typeof win.queueMicrotask === 'function' ? win.queueMicrotask.bind(win) : fn => Promise.resolve().then(fn);
  const timers = {set: (win.setTimeout || setTimeout).bind(win), clear: (win.clearTimeout || clearTimeout).bind(win)};

  function isCartForm(form) {
    if (form?.tagName !== 'FORM') return false;
    try {
      const base = doc.location?.href || 'https://shop.cutieroid-doll.com/';
      const url = new URL(form.getAttribute('action') || '', base);
      return /\/cart\/add\/?$/.test(url.pathname) && url.origin === new URL(base).origin && (form.getAttribute('method') || 'get').toLowerCase() === 'post';
    } catch { return false; }
  }

  function controls(form, name) {
    const inside = [...form.querySelectorAll('input, select, textarea')];
    if (form.id) for (const node of doc.querySelectorAll('input[form], select[form], textarea[form]')) if (node.getAttribute('form') === form.id && !inside.includes(node)) inside.push(node);
    return inside.filter(node => node.getAttribute('name') === name);
  }

  function matchesVariant(form) {
    const ids = controls(form, 'id').filter(node => !node.disabled);
    return ids.length > 0 && ids.every(node => valueOf(node) === VARIANT);
  }

  function phase(context) {
    const time = now();
    if (!Number.isFinite(time)) return 'unavailable';
    return time < context.start ? 'scheduled' : time >= context.end ? 'closed' : 'open';
  }

  function context() {
    const candidates = [...doc.querySelectorAll(WIDGET)].map(widget => {
      const result = readWidget(widget);
      const details = widget.querySelector('[data-cp-details]');
      if (details) details.hidden = !result.active;
      const editor = widget.querySelector('[data-cp-editor-status]');
      if (editor) {
        editor.hidden = result.active;
        setText(editor, result.reason === 'disabled' ? '専用予約は無効です。既存の購入フォームは変更していません。' : '専用予約の設定・正式バリエーションへのプラン割当・全額前払いの価格を確認してください。既存の購入フォームは変更していません。');
      }
      if (result.active) {
        const status = phase(result);
        setText(widget.querySelector('[data-cp-status]'), status === 'open' ? '予約受付中です。下記の条件をご確認ください。' : status === 'scheduled' ? `予約受付開始：${japanDateTime(result.start)}` : status === 'closed' ? '予約受付は終了しました。' : '日時を確認できません。ページを再読み込みしてください。');
        setText(widget.querySelector('[data-cp-cancellation]'), `決済完了後${result.c.cancelHours}時間と${japanDateTime(result.cutoff)}の、いずれか早い日時まで。`);
      }
      return result;
    }).filter(item => item.active);
    // Conflicting server-rendered configurations must not select an arbitrary plan.
    return candidates.length && candidates.every(item => item.fingerprint === candidates[0].fingerprint) ? candidates[0] : null;
  }

  function buttons(form) {
    const items = [...form.querySelectorAll('button, input[type="submit"], input[type="image"]')];
    if (form.id) for (const node of doc.querySelectorAll('button[form], input[form]')) if (node.getAttribute('form') === form.id && !items.includes(node)) items.push(node);
    return items.filter(node => node.tagName === 'BUTTON' ? !['button', 'reset'].includes((node.getAttribute('type') || 'submit').toLowerCase()) : ['submit', 'image'].includes((node.getAttribute('type') || '').toLowerCase()));
  }

  function stickyForm(sticky) {
    if (!sticky || sticky.getAttribute('data-product-id') !== PRODUCT || sticky.getAttribute('data-current-variant-id') !== VARIANT) return null;
    const section = sticky.getAttribute('data-section-id');
    // Match Horizon's #getProductForm section scope before looking at the product.
    const scope = sticky.closest('.shopify-section') || doc;
    const candidates = [...scope.querySelectorAll('product-form-component form, product-form form')].filter(form => {
      const wrapper = form.closest('product-form-component, product-form');
      return wrapper?.getAttribute('data-product-id') === PRODUCT && (!section || wrapper.getAttribute('data-section-id') === section) && isCartForm(form) && matchesVariant(form);
    });
    // An unrelated or ambiguous quick-add widget must not borrow another form's consent.
    return candidates.length === 1 ? candidates[0] : null;
  }

  function purchaseButtons(form) {
    const items = buttons(form);
    for (const sticky of doc.querySelectorAll(STICKY)) if (stickyForm(sticky) === form) {
      for (const button of sticky.querySelectorAll('button[type="button"][ref="addToCartButton"]')) if (!items.includes(button)) items.push(button);
    }
    return items;
  }

  function onButtonMutations(records) {
    let changed = false;
    for (const record of records) {
      const entry = buttonOwners.get(record.target);
      if (!entry) continue;
      const key = record.attributeName === 'disabled' ? 'disabled' : 'aria';
      const value = key === 'disabled' ? record.target.disabled : record.target.getAttribute('aria-disabled');
      if (entry[key] !== value) { entry[key] = value; changed = true; }
    }
    if (changed) queueRefresh();
  }

  function flushButtonMutations() { if (buttonObserver) onButtonMutations(buttonObserver.takeRecords()); }

  function writeButton(button, disabled, aria) {
    // Callers flush external changes before computing their desired state.
    // Discard only our synchronous writes, so a theme's later disabled/loading
    // change remains its own lock, even while our preorder lock is already on.
    if (button.disabled !== disabled) button.disabled = disabled;
    if (aria === null) { if (button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled'); }
    else setAttribute(button, 'aria-disabled', aria);
    buttonObserver?.takeRecords();
  }

  function releaseButton(state, button) {
    flushButtonMutations();
    const entry = state.buttons.get(button);
    state.buttons.delete(button);
    if (!entry) return;
    entry.owners.delete(state);
    if (entry.owners.size) return;
    writeButton(button, entry.disabled, entry.aria);
    buttonOwners.delete(button);
  }

  function secureButtons(state) {
    flushButtonMutations();
    const current = new Set(purchaseButtons(state.form));
    for (const button of [...state.buttons.keys()]) if (!current.has(button)) releaseButton(state, button);
    for (const button of current) {
      let entry = buttonOwners.get(button);
      if (!entry) {
        entry = {disabled: button.disabled, aria: button.getAttribute('aria-disabled'), owners: new Set()};
        buttonOwners.set(button, entry);
        buttonObserver?.observe(button, {attributes: true, attributeFilter: ['disabled', 'aria-disabled']});
      }
      entry.owners.add(state);
      state.buttons.set(button, entry);
      const blocked = [...entry.owners].some(owner => phase(owner.context) !== 'open' || !owner.checkbox.checked);
      const unavailable = button.closest(STICKY)?.getAttribute('data-variant-available') === 'false';
      const busy = button.getAttribute('aria-busy') === 'true' || state.form.closest('product-form-component, product-form')?.getAttribute('aria-busy') === 'true';
      const disabled = blocked || entry.disabled || unavailable || busy;
      writeButton(button, disabled, disabled ? 'true' : entry.aria);
    }
  }

  function paymentRoots(form) {
    const wrapper = form.closest('product-form-component, product-form');
    // Never suppress controls belonging to a neighboring, different product form.
    if (wrapper && [...wrapper.querySelectorAll('form')].filter(isCartForm).every(matchesVariant)) return [wrapper];
    return [form];
  }

  function releasePayment(state, node) {
    state.payment.delete(node);
    const entry = paymentOwners.get(node);
    if (!entry) return;
    entry.owners.delete(state);
    if (entry.owners.size) return;
    const old = entry.original;
    if (!old.hidden) node.removeAttribute('hidden');
    if (!old.inert) node.removeAttribute('inert');
    if (!old.blocked) node.classList.remove('cutieroid-preorder-accelerated-hidden');
    old.aria === null ? node.removeAttribute('aria-hidden') : node.setAttribute('aria-hidden', old.aria);
    paymentOwners.delete(node);
  }

  function secureFields(state) {
    for (const field of controls(state.form, PLAN_NAME)) {
      if (field === state.planField) continue;
      if (!state.otherPlans.has(field)) state.otherPlans.set(field, field.disabled);
      if (!field.disabled) field.disabled = true;
    }
    if (state.planField.parentNode !== state.form) state.form.append(state.planField);
    state.planField.disabled = false;
    if (valueOf(state.planField) !== state.context.plan) state.planField.value = state.context.plan;
    setAttribute(state.planField, 'value', state.context.plan);
    const paymentNodes = new Set(paymentRoots(state.form).flatMap(root => [...root.querySelectorAll(ACCELERATED)]));
    for (const old of [...state.payment]) if (!paymentNodes.has(old)) releasePayment(state, old);
    for (const node of paymentNodes) {
      if (!paymentOwners.has(node)) paymentOwners.set(node, {owners: new Set(), original: {hidden: node.hasAttribute('hidden'), aria: node.getAttribute('aria-hidden'), inert: node.hasAttribute('inert'), blocked: node.classList.contains('cutieroid-preorder-accelerated-hidden')}});
      paymentOwners.get(node).owners.add(state);
      state.payment.add(node);
      if (!node.hidden) node.hidden = true;
      setAttribute(node, 'aria-hidden', 'true');
      if (!node.hasAttribute('inert')) node.setAttribute('inert', '');
      node.classList.add('cutieroid-preorder-accelerated-hidden');
    }
    secureButtons(state);
  }

  function cleanup(state) {
    state.form.removeEventListener('formdata', state.onFormData);
    state.region.remove();
    state.planField.remove();
    for (const [field, disabled] of state.otherPlans) field.disabled = disabled;
    for (const button of [...state.buttons.keys()]) releaseButton(state, button);
    for (const node of [...state.payment]) releasePayment(state, node);
    states.delete(state.form);
  }

  function attach(form, current) {
    let state = states.get(form);
    if (!current || !isCartForm(form) || !matchesVariant(form)) {
      if (state) cleanup(state);
      return null;
    }
    if (state && (state.context.fingerprint !== current.fingerprint || state.region.parentElement !== form)) { cleanup(state); state = null; }
    if (!state) {
      const region = doc.createElement('div');
      region.className = 'cutieroid-preorder-consent';
      region.setAttribute('data-cp-owned', 'consent');
      const label = doc.createElement('label');
      const checkbox = doc.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.required = true;
      checkbox.setAttribute('required', '');
      checkbox.name = CONSENT_NAME;
      checkbox.value = `同意済み（予約条件v${current.c.version}）`;
      checkbox.checked = false;
      checkbox.setAttribute('data-cp-consent', '');
      const text = doc.createElement('span');
      text.textContent = `予約商品であること、全額前払い、発売・配送予定、およびキャンセル期限（決済完了後${current.c.cancelHours}時間または最終受付期限の早い方）を確認し、予約条件に同意します。`;
      label.append(checkbox, text);
      const message = doc.createElement('p');
      message.setAttribute('role', 'alert');
      message.setAttribute('data-cp-message', '');
      message.hidden = true;
      region.append(label, message);
      const planField = doc.createElement('input');
      planField.type = 'hidden';
      planField.name = PLAN_NAME;
      planField.value = current.plan;
      planField.setAttribute('data-cp-owned', 'plan');
      let before = buttons(form).find(button => form.contains(button));
      while (before && before.parentElement !== form) before = before.parentElement;
      form.insertBefore(region, before || null);
      form.append(planField);
      state = {form, context: current, region, checkbox, message, planField, otherPlans: new Map(), payment: new Set(), buttons: new Map()};
      state.onFormData = event => {
        if (!event.formData) return;
        if (!matchesVariant(form)) {
          if (event.formData.get(PLAN_NAME) === state.context.plan) event.formData.delete(PLAN_NAME);
          event.formData.delete(CONSENT_NAME);
          cleanup(state);
          return;
        }
        // Horizon may construct FormData itself; keep one exact native plan ID.
        event.formData.delete(PLAN_NAME);
        event.formData.append(PLAN_NAME, state.context.plan);
        event.formData.delete(CONSENT_NAME);
        if (state.checkbox.checked) event.formData.append(CONSENT_NAME, state.checkbox.value);
      };
      form.addEventListener('formdata', state.onFormData);
      states.set(form, state);
    }
    secureFields(state);
    return state;
  }

  function refresh() {
    if (disposed) return;
    stats.refreshes++;
    queued = false;
    const current = context();
    for (const state of [...states.values()]) if (!state.form.isConnected || !current || !isCartForm(state.form) || !matchesVariant(state.form)) cleanup(state);
    if (current) for (const form of doc.querySelectorAll('form')) if (isCartForm(form)) attach(form, current);
    if (timer !== null) timers.clear(timer);
    timer = null;
    if (autoTick && current && Number.isFinite(now()) && phase(current) !== 'closed') {
      const boundary = phase(current) === 'scheduled' ? current.start : current.end;
      timer = timers.set(refresh, Math.max(1, Math.min(60000, boundary - now() + 1)));
    }
  }

  function queueRefresh() { if (!queued && !disposed) { queued = true; enqueue(refresh); } }

  function formFor(node) {
    const target = element(node);
    const external = target?.closest('[form]');
    return (external ? doc.getElementById(external.getAttribute('form')) : target?.closest('form')) || stickyForm(target?.closest(STICKY));
  }

  function deny(event, state, message, focus = false) {
    event.preventDefault();
    event.stopImmediatePropagation();
    setText(state.message, message);
    state.message.hidden = false;
    if (focus) state.checkbox.focus();
  }

  function guard(event, state) {
    secureFields(state);
    const status = phase(state.context);
    if (status !== 'open') deny(event, state, status === 'scheduled' ? `予約は${japanDateTime(state.context.start)}から受け付けます。` : status === 'closed' ? '予約受付は終了しました。' : '日時を確認できません。ページを再読み込みしてください。');
    else if (!state.checkbox.checked) deny(event, state, '予約条件を確認し、同意欄にチェックを入れてください。', true);
    else state.message.hidden = true;
  }

  function onClick(event) {
    const target = element(event.target);
    if (!target) return;
    const current = context();
    const form = formFor(target);
    let state = form && attach(form, current);
    const accelerated = target.closest(ACCELERATED);
    if (accelerated && !state) state = [...states.values()].find(item => paymentRoots(item.form).some(root => root.contains(accelerated)));
    if (!state) return;
    if (accelerated) { deny(event, state, '予約条件をご確認のうえ、通常の予約注文ボタンをお使いください。'); return; }
    const button = target.closest('button, input');
    if (button && purchaseButtons(state.form).includes(button)) guard(event, state);
  }

  function onSubmit(event) { const state = attach(event.target, context()); if (state) guard(event, state); }
  function onInput(event) {
    const form = formFor(event.target);
    if (!form) return;
    const state = attach(form, context());
    if (state && event.target === state.checkbox) {
      state.message.hidden = true;
      secureFields(state);
    }
  }

  const Observer = win.MutationObserver;
  const buttonObserver = typeof Observer === 'function' ? new Observer(onButtonMutations) : null;
  const observer = typeof Observer === 'function' ? new Observer(records => {
    for (const record of records) {
      const target = element(record.target);
      if (target?.closest(OWNED)) continue;
      if (record.type === 'attributes') { queueRefresh(); return; }
      const changed = [...record.addedNodes, ...record.removedNodes].filter(node => node.nodeType === 1 && !node.matches(OWNED));
      if (!changed.length) continue;
      if (target?.closest(`form, product-form-component, product-form, ${STICKY}`) || changed.some(node => node.matches(`form, ${WIDGET}, ${ACCELERATED}, ${STICKY}`) || node.querySelector(`form, ${WIDGET}, ${ACCELERATED}, ${STICKY}`))) { queueRefresh(); return; }
    }
  }) : null;
  observer?.observe(doc.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['action', 'method', 'name', 'value', 'form', 'data-config', 'data-variant-id', 'data-allocations', 'data-variant-price', 'data-product-id', 'data-shop-domain', 'data-current-variant-id', 'data-variant-available', 'data-section-id', 'aria-busy']});
  eventRoot.addEventListener('click', onClick, true);
  eventRoot.addEventListener('submit', onSubmit, true);
  doc.addEventListener('change', onInput, true);
  doc.addEventListener('input', onInput, true);
  doc.addEventListener('visibilitychange', queueRefresh);
  refresh();
  return {refresh, stats, get attachedForms() { return states.size; }, destroy() {
    disposed = true;
    observer?.disconnect();
    flushButtonMutations();
    buttonObserver?.disconnect();
    if (timer !== null) timers.clear(timer);
    for (const state of [...states.values()]) cleanup(state);
    eventRoot.removeEventListener('click', onClick, true);
    eventRoot.removeEventListener('submit', onSubmit, true);
    doc.removeEventListener('change', onInput, true);
    doc.removeEventListener('input', onInput, true);
    doc.removeEventListener('visibilitychange', queueRefresh);
  }};
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const key = Symbol.for('cutieroid.preorder.controller.v1');
  if (!window[key]) window[key] = createPreorderController(document);
}
